<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：営業所別分析画面
 *
 * 作成者：2021/11/26 ノア）黄
 * 最終修正者：2022/02/12 ノア）虞
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button
            class="backButton"
            @click="onBackButtonClick"
            v-tooltip="{
              content: `${$t('master.tooltipCommon.back')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon
          ></el-button>
          <span class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_012"></tti-svg-icon>
            {{ $t('operationalPerformance.analysisByBusinessOfficesTitle') }}
          </span>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <div class="tti-lr-container">
        <!-- 所属選択 -->
        <div class="left tti-tree">
          <div class="tti-content-title">
            <span>{{ $t('operationalPerformance.choosedOffice') }}</span>
          </div>
          <div class="content">
            <el-tree
              :data="officeOptions"
              show-checkbox
              node-key="id"
              default-expand-all
              :check-on-click-node="true"
              :check-strictly="true"
              :expand-on-click-node="false"
              ref="businessOfficeTree"
              @check-change="onSelectedOfficeChange"
            >
            </el-tree>
          </div>
        </div>
        <!-- 集計期間選択 -->
        <div class="right tti-tree">
          <div class="tti-content-title">
            <span>{{ $t('operationalPerformance.aggregateSelection') }}</span>
          </div>
          <div class="content content-height">
            <el-form :model="aggregateSelection" label-width="100px" class="aggregateSelectionForm">
              <el-form-item prop="phoneNumber" :label="$t('operationalPerformance.operationStartDate')">
                <el-date-picker
                  v-model="aggregateSelection.operationStartDay"
                  type="date"
                  size="small"
                  format="yyyy/MM/dd"
                  value-format="yyyy/MM/dd"
                  :picker-options="pickerOptions"
                  :editable="false"
                  :clearable="false"
                  v-tooltip="{
                    content: `${$t('master.tooltipCommon.operationStartDayTip')}`,
                    placement: 'bottom-start',
                    delay: { show: 1000, hide: 0 }
                  }"
                ></el-date-picker>
              </el-form-item>
              <el-form-item prop="name" :label="$t('operationalPerformance.operationEndDate')">
                <el-date-picker
                  v-model="aggregateSelection.operationEndDay"
                  type="date"
                  size="small"
                  format="yyyy/MM/dd"
                  value-format="yyyy/MM/dd"
                  :picker-options="pickerOptions"
                  :editable="false"
                  :clearable="false"
                  v-tooltip="{
                    content: `${$t('master.tooltipCommon.operationEndDayTip')}`,
                    placement: 'bottom-start',
                    delay: { show: 1000, hide: 0 }
                  }"
                ></el-date-picker>
              </el-form-item>
            </el-form>
          </div>
          <!-- 実績データ選択 -->
          <div class="tti-content-title">
            <span>{{ $t('operationalPerformance.actualDataSelection') }}</span>
          </div>
          <div class="content content-height">
            <el-checkbox
              class="displayDeleteDataCheckbox"
              v-model="isDeleteDataDisplay"
              v-tooltip="{
                content: `${$t('operationalPerformance.deleteDataDisplayTip')}`,
                placement: 'bottom-start',
                delay: { show: 1000, hide: 0 }
              }"
            >
              {{ $t('operationalPerformance.deleteDataDisplayTip') }}
            </el-checkbox>
          </div>
        </div>
      </div>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 印刷 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onPrintClick"
          v-tooltip="{
            content: `${$t('operationalPerformance.outputPDF')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
        >
          <tti-svg-icon icon-name="he_008"></tti-svg-icon>
          {{ $t('buttons.print') }}
        </el-button>
      </div>
      <div class="tti-bottom-right-area">
        <!-- 出力 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onOutputClick"
          v-tooltip="{
            content: `${$t('operationalPerformance.outputCSV')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
        >
          <tti-svg-icon icon-name="su_003"></tti-svg-icon>
          {{ $t('buttons.output') }}
        </el-button>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator'
import { Tree } from 'element-ui'
import { uniqWith, isEqual, cloneDeep } from 'lodash'

import { TreeData, AggregateSelection, AllocationOfficeItem } from '@/models'
import { getSalesOfficeAllocationMasterData, opPerfOfcCSVDownload, opPerfOfcPDFDownload } from '@/store/apis'
import HelperService from '@/service/helperService'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { i18n } from '@/locales'
import { isDateAfter, isDateDayOver, pickerOptions } from '@/utils/dateUtils'
import { DeleteType, MIMEType } from '@/commons/constants'
import { common } from '@/store/modules'

/**
 * 営業所別分析画面
 *
 * @class
 */
@Component({
  name: 'op-perf-ofc'
})
export default class OP_PERF_OFC extends Vue {
  /**
   * タイムセレクタをクリックしたときの処理
   */
  pickerOptions = pickerOptions

  /**
   * 削除したGPSトラッカー表示用フラグ
   */
  isDeleteDataDisplay = false

  /**
   * 選択中の営業所リスト
   */
  checkedKeys: string[] = []

  /**
   * 集計期間情報
   */
  aggregateSelection: AggregateSelection = {
    operationStartDay: '',
    operationEndDay: ''
  }

  /**
   * 営業所情報
   */
  officeOptions: TreeData[] = []

  /**
   * 営業所割当マスタ
   */
  officeMasterData: AllocationOfficeItem[] = []

  /**
   * 営業所選択変更（WATCH用）
   *
   * @returns {number} 営業所選択変更（WATCH用）
   */
  get favOfficeListChange(): number {
    return common.favOfficeListChange
  }

  /**
   * 営業所割当マスタを取得する
   *
   * @returns {void}
   */
  @Watch('favOfficeListChange')
  onFavOfficeListChange(): void {
    this.initOfficeOptions()
  }

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  mounted(): void {
    this.$log.debug('OP_PERF_OFC > mounted')
    this.fetchSalesOfficeAllocationMaster()
  }

  /**
   * 選択を初期化する
   *
   * @returns {void}
   */
  initOfficeOptions(): void {
    let parentList: TreeData[] = []
    const officeMasterData = this.officeMasterData.filter(item => common.favOfficeList.includes(item.businessOfficeId))

    const allOfficeOptions = HelperService.formatofficeData(this.officeMasterData)

    officeMasterData.forEach(item => {
      const perParentData = cloneDeep(HelperService.getParentData(allOfficeOptions, item.businessOfficeId))
      parentList = parentList.concat(perParentData)
    })

    parentList = parentList.filter(item => !common.favOfficeList.includes(item.id))
    const parentOfficeMasterData: AllocationOfficeItem[] = cloneDeep(
      this.officeMasterData.filter(item => {
        let isTrue = false
        for (const iterator of parentList) {
          if (iterator.id === item.businessOfficeId) {
            isTrue = true
            break
          }
        }
        return isTrue
      })
    )

    parentOfficeMasterData.forEach(item => {
      item.disabled = true
    })
    let totleArr = parentOfficeMasterData.concat(officeMasterData).sort((a, b) => {
      if (a.businessOfficeCode && b.businessOfficeCode) {
        if (a.businessOfficeCode > b.businessOfficeCode) return 1
        if (a.businessOfficeCode < b.businessOfficeCode) return -1
      }
      return 0
    })
    totleArr = uniqWith(totleArr, isEqual)
    this.officeOptions = HelperService.formatofficeData(totleArr)
  }

  /**
   * 営業所割当マスタを取得する
   *
   * @returns {void}
   */
  async fetchSalesOfficeAllocationMaster(): Promise<void> {
    try {
      const masters = await getSalesOfficeAllocationMasterData()
      this.officeMasterData = (masters.businessOfficeList as AllocationOfficeItem[]) || []
      this.initOfficeOptions()
    } catch (e) {
      this.$log.debug('OP_PERF_OFC > fetchSalesOfficeAllocationMaster: e=', e)
      throw e
    }
  }

  /**
   * 選択中の営業所情報を変更した時の処理
   *
   * @param {TreeData} data 選択中の情報
   * @param {boolean} isSelected 選択されたフラグ
   * @returns {void}
   */
  onSelectedOfficeChange(data: TreeData, isSelected: boolean): void {
    this.checkedKeys = (this.$refs.businessOfficeTree as Tree).getCheckedKeys()
    this.$log.debug('OP_PERF_OFC > onSelectedOfficeChange', this.checkedKeys)
    if (!data.children?.length) {
      return
    }
    this.checkNode(data.children, isSelected)
  }

  /**
   * すべての下位層を選択
   *
   * @param {TreeData[]} arr 選択中の情報リスト
   * @param {boolean} isSelected 選択されたフラグ
   * @returns {void}
   */
  checkNode(arr: TreeData[], isSelected: boolean): void {
    arr.forEach((item: TreeData) => {
      ;(this.$refs.businessOfficeTree as Tree).setChecked(item, isSelected, false)
      if (item.children !== undefined) {
        this.checkNode(item.children, isSelected)
      }
    })
  }

  /**
   * 戻るボタンをクリックした時の処理
   *
   * @returns {void}
   */
  onBackButtonClick(): void {
    window.history.back()
    this.$log.debug('OP_PERF_OFC > onBackButtonClick')
  }

  /**
   * 出力ボタンをクリックした時の処理
   *
   * @returns {void}
   */
  onOutputClick(): void {
    this.$log.debug('OP_PERF_OFC > onOutputClick')

    if (!this.correlationCheck()) return

    const confirmCallback = () => {
      this.csvOutputAPI()
    }

    msgBoxService.confirm(i18n.t('operationalPerformance.I00160').toString(), confirmCallback)
  }

  /**
   * CSV出力API
   *
   * @returns {void}
   */
  async csvOutputAPI(): Promise<void> {
    const params = {
      businessOfficeIdList: this.checkedKeys,
      operationStartDate: this.aggregateSelection.operationStartDay,
      operationEndDate: this.aggregateSelection.operationEndDay,
      deleteFlg: this.isDeleteDataDisplay ? DeleteType.deleteData : DeleteType.notdeleteData
    }

    const res = await opPerfOfcCSVDownload(params)
    HelperService.download(res.fileName, res.data, MIMEType.zip)
  }

  /**
   * PDF出力API
   *
   * @returns {void}
   */
  async pdfOutputAPI(): Promise<void> {
    const params = {
      businessOfficeIdList: this.checkedKeys,
      operationStartDate: this.aggregateSelection.operationStartDay,
      operationEndDate: this.aggregateSelection.operationEndDay,
      deleteFlg: this.isDeleteDataDisplay ? DeleteType.deleteData : DeleteType.notdeleteData
    }

    const res = await opPerfOfcPDFDownload(params)
    HelperService.download(res.fileName, res.data, MIMEType.pdf)
  }

  /**
   * 印刷ボタンをクリックした時の処理
   *
   * @returns {void}
   */
  onPrintClick(): void {
    if (!this.correlationCheck()) return

    const confirmCallback = () => {
      this.pdfOutputAPI()
    }

    msgBoxService.confirm(i18n.t('operationalPerformance.I00160').toString(), confirmCallback)
    this.$log.debug('OP_PERF_OFC > onPrintClick')
  }

  /**
   * 相関チェックを行う
   *
   * @returns {boolean} 相関チェック結果
   */
  correlationCheck(): boolean {
    if (this.checkedKeys.length === 0) {
      msgBoxService.info(
        i18n.t('vaildate.common.selectRequired', [i18n.t('operationalPerformance.businessOfficeMaster')]).toString()
      )
      return false
    }

    if (!this.aggregateSelection.operationStartDay) {
      msgBoxService.info(
        i18n.t('vaildate.common.selectRequired', [i18n.t('operationalPerformance.operationStartDay')]).toString()
      )
      return false
    }

    if (!this.aggregateSelection.operationEndDay) {
      msgBoxService.info(
        i18n.t('vaildate.common.selectRequired', [i18n.t('operationalPerformance.operationEndDay')]).toString()
      )
      return false
    }

    if (isDateAfter(this.aggregateSelection.operationStartDay, this.aggregateSelection.operationEndDay)) {
      msgBoxService.info(
        i18n
          .t('vaildate.common.dateRange', [
            i18n.t('operationalPerformance.operationStartDay'),
            i18n.t('operationalPerformance.operationEndDay')
          ])
          .toString()
      )
      return false
    }

    if (isDateDayOver(this.aggregateSelection.operationEndDay, this.aggregateSelection.operationStartDay, 31)) {
      msgBoxService.info(i18n.t('vaildate.common.differenceInDays', [31]).toString())
      return false
    }

    return true
  }
}
</script>

<style lang="scss" scoped>
::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

.aggregateSelectionForm {
  width: 300px;
}

.tti-tree {
  overflow: auto;

  .tti-content-title {
    font-size: $FS02;
  }

  .content-height {
    height: 120px !important;
  }

  ::v-deep ul {
    padding-left: 24px;
  }

  ::v-deep .content {
    > ul {
      padding-left: 0;
      margin-top: 0;
      white-space: nowrap;
    }
  }
}

::v-deep .el-tree-node .el-tree-node__content {
  background-color: #fff;
}
</style>
